import { DayObject } from "./date"
import { PlaceRef } from "./entity"
import { FlightBadgeContract } from "./flights/flight-badge"
import type { Room } from "./trip"

export interface HotelInfo {
  entity_ref: PlaceRef // e.g., "ho-4TBOV0mL"
  name: string
}

export interface RoomInfo {
  entity_ref: string // e.g., "rm-12345"
  name: string
  image_url?: string | null // first room photo, when available
}

export type DatesSelectedBy = "user" | "trip" | "assistant"

export interface DateInfo {
  check_in: string // ISO date format YYYY-MM-DD
  check_out: string // ISO date format YYYY-MM-DD
  nights: number
  selected_by?: DatesSelectedBy | null // who authored the dates; null on older quotes
}

export interface PeopleInfo {
  adults: number
  children: number
  child_ages?: number[]
  num_rooms?: number // Optional for backwards compatibility
}

export interface BudgetInfo {
  price: number
  currency: string
}

export interface PriceInfo {
  /** absent on a points quote (HotelQuote.points), which the card shows instead */
  per_night: number
  per_night_with_fees?: number | null
  total: number
  total_with_fees?: number | null
  currency: string
  /** True when the quote was created with taxes & fees included (i.e. per_night_with_fees is the canonical quoted price).
   *  Absent on older quotes — treat as false. */
  quoted_price_includes_fees?: boolean
}

export interface LivePrice {
  available: boolean
  per_night?: number | null
  per_night_with_fees?: number | null
  total?: number | null
  total_with_fees?: number | null
  currency?: string | null
  provider?: string | null
  supplier?: string | null
  reason?: string | null // 'room_unavailable' | 'hotel_unavailable' | 'fetch_failed'
  rooms_remaining?: number | null // real inventory count (Priceline only); null otherwise
}

export interface PriceQuote {
  ref: string // e.g., "hq-4TBOV0mL"
  type: "hotel" | "flight" | "web"
  suggestion_reason?: string
  expires_at: { epoch_millis: number }
  created_at: { epoch_millis: number }
  is_expired: boolean
}

export interface HotelQuote extends PriceQuote {
  type: "hotel"
  booking_url: string
  hotel: HotelInfo
  room: RoomInfo | null
  dates: DateInfo
  people: PeopleInfo
  price: PriceInfo
  persona?: string
  provider: string
  budget_per_night?: BudgetInfo // Optional user budget for comparison
  /** Present when the quote is a points (award) rate read on the hotel's own site, which has no cash price */
  points?: {
    per_stay: number
    per_night: number
    fee?: string | null
    room_name?: string
    refundable?: boolean
    /** the rate's cancellation terms as the hotel's site words them */
    cancellation?: string
    /** the best cash rate the same read of the hotel's site showed, before taxes; the points are weighed against it */
    cash?: {
      per_night: number
      total: number
      currency: string
      rate_name?: string
      refundable?: boolean
    }
  }
  /** Present when the quote is for a short-term rental listing read on its site (e.g. Airbnb) */
  str_listing?: { cancellation?: string | null; pets?: number }
  live_price?: LivePrice // Present when fetched with fetch_live_price=true
  /** Points math for the stay; only the quote's owner gets it, and only when they care about points */
  loyalty?: QuoteLoyalty
  /** whether this quote is a good deal for what its owner cares about (Mindtrip staff only for now) */
  value?: QuoteValue
}

/** Where a loyalty fact came from in the rules corpus */
export interface LoyaltySource {
  url?: string
  quote?: string
}

/** known, or conditional (true only if `condition` holds), or unknown (with `reason`) */
export interface LoyaltyFact {
  status: "known" | "conditional" | "unknown"
  condition?: string
  reason?: string
  source?: LoyaltySource
}

export type LoyaltyInterest = "stated" | "asked" | "member"

/** The award price read on the hotel's own site (or given by the traveler), weighed against this quote's cash price */
export interface LoyaltyRedemption extends LoyaltyFact {
  award_points_per_night?: number
  points_for_stay?: number
  /** the traveler, or the site the award was read on */
  award_source?: string
  cash_per_night?: number
  against?: "all_in" | "base"
  /** the award's points at the typical point value */
  points_worth_per_night?: number
  cents_per_point?: number
  /** cents of cash each point stands in for on this award */
  redemption_cents?: number
  /** the program's published range */
  typical_cents?: number[]
  verdict?: "poor" | "fair" | "good" | "exceptional"
  /** the redemption values each verdict covers, in cents a point, worst first */
  verdict_bands?: {
    verdict: "poor" | "fair" | "good" | "exceptional"
    from_cents?: number
    to_cents?: number
  }[]
  meaning?: string
  /** recomputed by the backend at the live price because it moved */
  at_live_price?: boolean
  rate_name?: string
  /** a cash fee the points rate still charges */
  fee?: string
  value_sources?: { name: string; cents: number }[]
}

/** The lowest refundable member rate read on the hotel's own site, before taxes */
export interface LoyaltyDirectRate extends LoyaltyFact {
  rate_name?: string
  cash_per_night?: number
  cash_total?: number
  non_member_cash_per_night?: number
  before_taxes?: boolean
  read_from?: string
  quote_url?: string
}

/** What the stay would earn booked direct at the member rate read on the hotel's site; unknown when it wasn't read */
export interface LoyaltyEarningDirect extends LoyaltyFact {
  points_per_dollar?: number
  on_base_spend?: number
  points?: number
  worth_usd?: number
  cents_per_point?: number
  spend_basis?: string
  elite_bonus?: LoyaltyFact & { percent?: number }
}

export interface LoyaltyCardEarning extends LoyaltyFact {
  card: string
  points_per_dollar?: number
  on_charged_spend?: number
  points?: number
  currency?: string
  spend_basis?: string
}

export interface LoyaltyTransfer extends LoyaltyFact {
  from: string
  ratio?: number
  bonus_ratio?: number
  bonus_ends?: DayObject
  lower_bound?: boolean
  increment?: number
  /** the bank points that cover the award */
  points_to_cover_award?: number
  /** their balance in the bank currency, when known */
  balance?: number
  /** the program points that balance becomes through this transfer, in whole increments */
  reachable_points?: number
  /** how many program points the reachable points fall short of the award */
  short_by?: number
}

/** Card points spent in one bank's travel portal on this stay, at this quote's price */
export interface LoyaltyPortalOption {
  from: string
  cents_per_point: number
  channel?: string
  balance?: number
  /** cards the rate needs, when we don't know they hold one */
  requires_cards?: string[]
  points: number
  covers_usd?: number
  gap_usd?: number
  source?: LoyaltySource
}

/** Always conditional: the portal sells at its own price, which with their balance there needs their sign-in */
export interface LoyaltyPortal extends LoyaltyFact {
  needs_sign_in?: boolean
  against?: "all_in" | "base"
  total_usd: number
  options: LoyaltyPortalOption[]
}

export interface LoyaltyStatusPerk extends LoyaltyFact {
  benefit: string
  value?: string
}

export interface QuoteLoyalty {
  interest: LoyaltyInterest[]
  /** the hotel's program, or for a hotel in no program (portal_only) the bank currency whose travel portal the facts price */
  program: string
  /** a hotel in no hotel program we know: the travel portal is the only fact */
  portal_only?: boolean
  /** the card's one sentence about paying with points, written by the backend */
  sentence: string
  /**
   * the question the card answers, from what the traveler asked: cash or points (redemption_benchmark), paying with points with or
   * without a known balance (covers, points_price), what their status gets them (perks), what the stay earns (earn),
   * card points in a bank's travel portal when there's no award to weigh (portal)
   */
  flavor?:
    | "redemption_benchmark"
    | "covers"
    | "points_price"
    | "perks"
    | "earn"
    | "portal"
  brand?: string
  computed_on: DayObject
  rules_as_of?: DayObject
  participation?: LoyaltyFact
  /** where and when the hotel's own rates were read; absent when they weren't */
  direct_read?: string
  redemption?: LoyaltyRedemption
  direct_rate?: LoyaltyDirectRate
  earning_direct?: LoyaltyEarningDirect
  card_earning: LoyaltyCardEarning[]
  transfers: LoyaltyTransfer[]
  status_perks: LoyaltyStatusPerk[]
  /** card points in each of their banks' travel portals, at this quote's price */
  portal?: LoyaltyPortal
  personal: {
    tier?: LoyaltyFact & {
      name: string
      valid_through?: DayObject
      lifetime?: boolean
    }
    balance?: { points: number; last_observed?: DayObject }
    balance_covers_award?: boolean
  }
}

export function roomFromHotelQuote(quote: HotelQuote): Room | undefined {
  if (!quote.people.child_ages) return undefined

  return {
    adult_count: quote.people.adults,
    child_ages: quote.people.child_ages,
    pet_count: 0,
  }
}

export interface FlightQuote extends PriceQuote {
  type: "flight"
  booking_url?: string | null // omitted in shared redacted quote responses; null for combo quotes
  tags: string[]
  badges?: FlightBadgeContract[]
  rejected: boolean
  // Heavy payload (is_combo, passengers, pricing, itineraries[]) lives at
  // GET /api/flights/quotes/:ref — see FlightQuoteDetails in
  // flight-quote-details.ts.
}

// The price of a quote's display (the quote_display_price OpenAPI schema). An award leads with its points, and its
// amount is only the taxes beside them; the quote's pricing still holds the same flights' cash fare.
export interface QuoteDisplayPrice {
  amount?: number
  currency?: string
  points?: { displayed: string; unit: string; program?: string }
  basis:
    | "nightly_average"
    | "stay_total"
    | "round_trip"
    | "one_way"
    | "per_person_round_trip"
    | "per_person"
    | "rental_total"
    | "per_day"
    | "multi_city"
}

// What a quote card shows (the quote_display OpenAPI schema); the web reads its price and facts, and an award flight's
// booking link and value call
export interface QuoteDisplay {
  price?: QuoteDisplayPrice
  /** Short qualifiers; an award quote's includes its cash fare, "Or $58.40 cash", and when it was seen, "Seen 5h ago" */
  facts?: string[]
  /** An award flight's program's own award search for its day, where the card books it with miles */
  booking_url?: string
  /** An award flight's cents-per-mile call, for its owner when they are staff */
  value?: QuoteValue
}

// A priced option a browser task found on a website. The API omits its null fields and its evidence.
export interface WebQuote extends PriceQuote {
  type: "web"
  booking_url: string
  option: {
    id: string
    kind:
      | "flight"
      | "hotel"
      | "vacation_rental"
      | "restaurant"
      | "transit"
      | "car_rental"
    name: string
    price: { amount: number; currency: string; basis: string }
    includesTaxesAndFees?: boolean
    seller?: string
    flight?: Record<string, unknown>
    stay?: Record<string, unknown>
    restaurant?: Record<string, unknown>
    transit?: Record<string, unknown>
    car?: Record<string, unknown>
    url: string
  }
}

// What GET /api/price_quotes/:ref returns: the ref prefix says which, but the
// client learns the kind from `type`, not from sniffing the ref
export type PriceQuoteResponse = HotelQuote | FlightQuote | WebQuote

export function isHotelQuote(
  quote: PriceQuoteResponse | undefined
): quote is HotelQuote {
  return quote?.type === "hotel"
}

export function isFlightQuote(
  quote: PriceQuoteResponse | undefined
): quote is FlightQuote {
  return quote?.type === "flight"
}

// HotelQuoteValue's calls for the quote card's value meter. The verdicts' words are in lib/quote-value-copy.ts.
export type ValueVerdict = "poor" | "fair" | "good" | "exceptional"

export interface QuoteValueScale {
  /** where this quote sits, 0 (worst) to 1 (best), placed by the backend */
  position: number
  /** the typical band on the same 0 to 1 scale */
  typical: [number, number]
  /** what the typical band is, in the signal's own units: "Typical 0.7–0.9¢ a point" */
  typical_label: string
}

/** A coverage call's picture: the award for the stay, their balance, and the bank transfer that makes up any gap */
export interface QuoteValueGauge {
  award: number
  balance: number
  transfer?: { from: string; points: number }
}

/** An earn call's picture: one night's award here, their balance, and what this stay earns booked direct */
export interface QuoteValueProgress {
  goal: number
  balance: number
  earned: number
}

/** A perks call's picture: the tier perks it names, and whether this booking keeps them */
export interface QuoteValuePerks {
  kinds: string[]
  kept: boolean
}

export interface QuoteValueSignal {
  kind:
    | "points"
    | "coverage"
    | "earn"
    | "perks"
    | "price"
    | "compare"
    | "flexibility"
    | "breakfast"
    | "budget"
  /** absent when we couldn't check, e.g. the hotel's site didn't load */
  verdict?: ValueVerdict
  /** what to do and the numbers behind it, in one short sentence: "pay cash: 0.57¢ a point" */
  detail: string
  /** the call in full when `detail` is terse beside its picture, for the tapped row and screen readers */
  full_detail?: string
  /** why we think they care: "you asked about points", "you usually book refundable" */
  because: string
  /** for a "how good" call (points value, price against the others); a yes/no call has none */
  scale?: QuoteValueScale
  gauge?: QuoteValueGauge
  progress?: QuoteValueProgress
  perks?: QuoteValuePerks
}

export interface QuoteValue {
  /** the calls worth showing this traveler, the one that matters most first */
  signals: QuoteValueSignal[]
}
